/* eslint-disable react-native/no-inline-styles */
import React, { useState } from 'react';
import { StyleSheet, View, TouchableOpacity, Dimensions } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { showAlert } from '@/store/global';
import PropTypes from 'prop-types';

import SociollaText from '@components/sociolla/SociollaText';
import Image from '@components/global/Image';
import RenderIf from '@components/global/RenderIf';
import CheckBox from '@components/form/CheckBox';
import Table from '@components/table/Table';
import TableMobile from '@components/table/TableMobile';

import Colors from '@global/colors';
import { isWeb, isNative, isMobileWeb } from '@global/screens';
import { escapeRegexChars } from '@global/helpers';
import { selectParams, getCurrentParams } from '@/store/params';
import store from '@/store';
import {
  REQUEST_PRODUCT_LISTING_ITEMS,
  REQUEST_PRODUCT_LISTING_ITEMS_FORM,
} from '@/constants/table-name';

const propTypes = {
  list: PropTypes.array,
  productCount: PropTypes.number,
  loading: PropTypes.bool,
  getProducts: PropTypes.func,
  isEditMode: PropTypes.bool,
  editProduct: PropTypes.func,
  deleteProduct: PropTypes.func,
};

const HEADER_DEKSTOP = [
  {
    head: 'Image',
    body: 'image',
    type: 'image',
  },
  {
    head: 'Product Name',
    body: 'ref_name',
  },
  {
    head: 'Attribute',
    body: 'variant',
  },
  {
    head: 'Category',
    body: 'category',
  },
  {
    head: 'Is Active',
    body: 'is_active',
    type: 'slot',
  },
];

const HEADER_DEKSTOP_EDIT = [
  {
    head: 'Image',
    body: 'image',
    type: 'image',
  },
  {
    head: 'Product Name',
    body: 'ref_name',
  },
  {
    head: 'Attribute',
    body: 'variant',
  },
  {
    head: 'Category',
    body: 'category',
  },
  {
    head: 'Is Active',
    body: 'is_active',
    type: 'slot',
  },
  {
    head: '',
    body: '',
    type: 'action_icon',
    actions: [
      {
        name: 'delete',
      },
    ],
  },
];

const defaultPhoto = require('@/assets/img/default/user-default.png');
const dimension = Dimensions.get('screen');

function TableProductListingItem(props) {
  const {
    list,
    productCount,
    loading,
    getProducts,
    isEditMode,
    editProduct,
    deleteProduct,
  } = props;
  const paramsState = useSelector(
    selectParams(
      isEditMode
        ? REQUEST_PRODUCT_LISTING_ITEMS_FORM
        : REQUEST_PRODUCT_LISTING_ITEMS,
    ),
  );

  const [searchValue, setSearchValue] = useState('');
  const [loadingMore, setLoadingMore] = useState(false);

  const dispatch = useDispatch();

  const onMapAction = ({ data, index, action }) => {
    if (action.name == 'delete') {
      dispatch(
        showAlert({
          show: true,
          title: 'Remove Product',
          description: 'This product will be deleted. Do you want to proceed?',
          onCallback: async (val) => {
            try {
              await deleteProduct(data);
            } catch (e) {
              console.log(e);
            }
          },
        }),
      );
    }
  };

  const doSearch = async (val, isMobile) => {
    if (isMobile) {
      setLoadingMore(true);
    }
    if (val) {
      setSearchValue(val);
    }
    const params = getCurrentParams(
      store.getState(),
      isEditMode
        ? REQUEST_PRODUCT_LISTING_ITEMS_FORM
        : REQUEST_PRODUCT_LISTING_ITEMS,
    );

    const finalParams = { ...params };
    if (finalParams?.filter?.keyword) {
      const keyword =
        finalParams.filter.keyword?.$regex ?? finalParams.filter.keyword;
      finalParams.filter['$or'] = [
        {
          name: { $regex: escapeRegexChars(keyword), $options: 'i' },
        },
        {
          'combination.reference': { $regex: keyword, $options: 'i' },
        },
        {
          'combination.ean_no': { $regex: keyword, $options: 'i' },
        },
        {
          'brand.name': { $regex: keyword, $options: 'i' },
        },
      ];
      delete finalParams.filter.keyword;
    }

    await getProducts({ ...finalParams });
    if (isMobile) {
      setLoadingMore(false);
    }
  };

  const handleActiveProduct = async (data) => {
    await editProduct(data);
  };

  const renderItem = ({ data, index }) => (
    <TouchableOpacity
      key={'list-product-' + data?._id?.toString()}
      style={styles.tableRowMobile}>
      <View style={{ marginRight: 10, alignSelf: 'flex-start' }}>
        <Image
          source={{ uri: `${data.image ? data.image : defaultPhoto}` }}
          style={{ width: 36, height: 36 }}
          width={36}
          height={36}
          defaultSource={defaultPhoto}
        />
      </View>
      <View style={{ flex: 1, paddingRight: 20 }}>
        <SociollaText
          type="medium"
          style={{ fontSize: 12, color: Colors.black1, paddingBottom: 8 }}>
          {data.brand.name}
        </SociollaText>
        <SociollaText
          style={{ fontSize: 12, color: Colors.black5, paddingBottom: 8 }}>
          {data.name}
        </SociollaText>
        <SociollaText
          type="medium"
          style={{ fontSize: 12, color: Colors.black1, paddingBottom: 8 }}>
          {data.combination.reference}
        </SociollaText>
        <SociollaText style={{ fontSize: 12, color: Colors.black5 }}>
          {data.variant}
        </SociollaText>
      </View>
      <View style={styles.rowActionMobile}>
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <CheckBox
            checked={data.is_active ? true : false}
            checkboxStyle={{ marginTop: 10, marginRight: 0 }}
            onChange={async () => await handleActiveProduct(data)}
          />
          <SociollaText key={`reference-${data?._id?.toString()}`}>
            {data.is_active ? 'Active' : 'Inactive'}
          </SociollaText>
        </View>
        <RenderIf condition={isEditMode}>
          <TouchableOpacity
            onPress={() =>
              onMapAction({ action: { name: 'delete' }, data, index })
            }>
            <Image
              width={20}
              height={20}
              svg={true}
              source={require('@assets/img/icons/trash-grey.svg')}
              style={{ height: 20, width: 20 }}
            />
          </TouchableOpacity>
        </RenderIf>
      </View>
    </TouchableOpacity>
  );

  return (
    <>
      <RenderIf condition={isWeb}>
        <Table
          name={
            isEditMode
              ? REQUEST_PRODUCT_LISTING_ITEMS_FORM
              : REQUEST_PRODUCT_LISTING_ITEMS
          }
          filterName={
            isEditMode
              ? REQUEST_PRODUCT_LISTING_ITEMS_FORM
              : REQUEST_PRODUCT_LISTING_ITEMS
          }
          structures={isEditMode ? HEADER_DEKSTOP_EDIT : HEADER_DEKSTOP}
          loading={loading}
          data={list}
          searchKey="keyword"
          searchPlaceholder="Search by Name, Reference, EAN"
          searchValue={searchValue}
          hasNoHide={true}
          hasCallback={false}
          totalData={productCount}
          skip={paramsState.skip}
          limit={paramsState.limit}
          onSearch={(val) => doSearch(val)}
          showFilter={false}
          onMapAction={onMapAction}
          slot={{
            is_active: ({ data, index }) => (
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <CheckBox
                  checked={data.is_active ? true : false}
                  checkboxStyle={{ marginTop: 10, marginRight: 0 }}
                  onChange={async () => await handleActiveProduct(data)}
                />
                <SociollaText key={`reference-${index}`}>
                  {data.is_active ? 'Active' : 'Inactive'}
                </SociollaText>
              </View>
            ),
          }}
          disablePointer
        />
      </RenderIf>
      <RenderIf condition={!isWeb}>
        <View
          style={
            isMobileWeb
              ? {
                  height: Math.max(
                    Dimensions.get('window').height - (isEditMode ? 260 : 90),
                    200,
                  ),
                }
              : {}
          }>
          <TableMobile
            useLegendList
            name={
              isEditMode
                ? REQUEST_PRODUCT_LISTING_ITEMS_FORM
                : REQUEST_PRODUCT_LISTING_ITEMS
            }
            filterName={
              isEditMode
                ? REQUEST_PRODUCT_LISTING_ITEMS_FORM
                : REQUEST_PRODUCT_LISTING_ITEMS
            }
            searchKey="keyword"
            placeholder="Search by Name, Reference, EAN"
            searchValue={searchValue}
            loading={loading}
            onSearch={(val) => doSearch(val)}
            skip={paramsState.skip}
            limit={paramsState.limit}
            tableData={list}
            totalData={productCount}
            loadingPagination={loadingMore}
            withLoadMore
            withFetch
            noResetOnRefresh
            onLoadMore={() => doSearch(searchValue, true)}
            showSearch={true}
            renderItem={renderItem}
            tableHeight={
              isMobileWeb
                ? undefined
                : dimension.height - (isEditMode ? 300 : 100)
            }
          />
        </View>
      </RenderIf>
    </>
  );
}

const styles = StyleSheet.create({
  tableRowMobile: {
    borderBottomColor: Colors.grey9,
    borderBottomWidth: 1,
    paddingVertical: 15,
    paddingHorizontal: 10,
    flexDirection: 'row',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  rowActionMobile: {
    height: isNative ? 80 : '100%',
    justifyContent: 'space-between',
    paddingRight: 30,
  },
});

TableProductListingItem.propTypes = propTypes;

export default React.memo(TableProductListingItem);
